import {Button, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, MenuItem} from '@material-ui/core';
import {ExpandMore} from '@material-ui/icons';
import {
  changeEnrollmentFile,
  changeEnrollmentType,
  changeServicingAgentNumber,
  uploadApplication,
  changeAccountNumber,
} from 'Agent/Accounts/ducks/enrollmentApplication';
import {getAgentNumberErrors} from 'Agent/Accounts/models/AgentNumber';
import {
  EnrollmentApplicationWithError,
  EnrollmentType,
  InvalidEnrollmentApplication,
  isValidApplication,
  ValidEnrollmentApplication,
} from 'Agent/Accounts/models/EnrollmentApplication';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Alert from 'Shared/components/Alert';
import FileDropzone from 'Shared/components/FileDropzone';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {ReducerState} from 'rootReducer';

type EnrollmentApplicationFormProps = WithSelectedAgencyProps & {
  onCancel: () => void;
  application: InvalidEnrollmentApplication | ValidEnrollmentApplication | EnrollmentApplicationWithError;
  accountNumber: string;
  servicingAgentNumber?: string;
  accountId: string;
};

export const EnrollmentApplicationForm = (props: EnrollmentApplicationFormProps) => {
  const dispatch = useDispatch();
  const {accountNumber, servicingAgentNumber, application, onCancel, selectedAgency} = props;
  const accountHasServicingAgent = Boolean(servicingAgentNumber);
  const shouldShowOrdinaryMailingAddress =
    application.enrollmentType && [EnrollmentType.ORDINARY, EnrollmentType.GROUP].includes(application.enrollmentType);
  const shouldShowGroupMailingAddress =
    application.enrollmentType && application.enrollmentType === EnrollmentType.GENELCO_GROUP;

  const {enable_portals_virus_scan, SIC_FileNet_Connectivity} = useSelector((state: ReducerState) => state.featureFlag);

  useEffect(() => {
    if (servicingAgentNumber) {
      dispatch(changeServicingAgentNumber(servicingAgentNumber));
    }
    if (accountNumber) {
      dispatch(changeAccountNumber(accountNumber));
    }
  }, []);

  return (
    <form className="upload-enrollment-application">
      <p>Please enter the file and enrollment type of the application below:</p>
      <p data-dd-privacy="mask">Account Number: {accountNumber}</p>
      <FileDropzone
        className="upload-enrollment-application__file-dropzone"
        file={application.file}
        onSelectFile={file => dispatch(changeEnrollmentFile(file))}
      />
      <i>* Files must be in TIF, PDF, or JPG format (10 mb)</i>
      <OutlinedSelect
        id={'upload-enrollment-type-select'}
        className="upload-enrollment-application__enrollment-type-select"
        variant="outlined"
        value={application.enrollmentType || ''}
        onChange={e => dispatch(changeEnrollmentType(parseInt(e.target.value, 10) as EnrollmentType))}
        label="Enrollment Type"
      >
        <MenuItem value={EnrollmentType.ORDINARY}>Ordinary</MenuItem>
        <MenuItem value={EnrollmentType.GROUP}>Group Voluntary</MenuItem>
        {!selectedAgency.isNewYork && <MenuItem value={EnrollmentType.GENELCO_GROUP}>Genelco Group</MenuItem>}
      </OutlinedSelect>
      {!accountHasServicingAgent && (
        <ValidatableTextField
          label="Servicing Agent"
          inputProps={{
            maxLength: 5,
          }}
          onChange={servicingAgentNumber => dispatch(changeServicingAgentNumber(servicingAgentNumber))}
          errors={getAgentNumberErrors(application.servicingAgentNumber)}
          value={application.servicingAgentNumber.value}
        />
      )}
      {shouldShowOrdinaryMailingAddress && (
        <ExpansionPanel className="upload-enrollment-application__mailing-info">
          <ExpansionPanelSummary expandIcon={<ExpandMore />} aria-label="Expand">
            Mailing Information
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <p>
              Ordinary and Group Voluntary requirements must be mailed to:
              <br />
              <br />
              New Business
              <br />
              4920 San Pablo Rd South
              <br />
              Suite 200C
              <br/>
              Jacksonville, FL 32224
            </p>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}
      {shouldShowGroupMailingAddress && (
        <ExpansionPanel className="upload-enrollment-application__mailing-info">
          <ExpansionPanelSummary expandIcon={<ExpandMore />} aria-label="Expand">
            Mailing Information
          </ExpansionPanelSummary>
          <ExpansionPanelDetails data-testid="genelco-mailing-information">
            <p>
              Genelco Group requirements must be mailed to:
              <br />
              <br />
              Genelco Group New Business
              <br />
              4920 San Pablo Rd South
              <br />
              Suite 200C
              <br/>
              Jacksonville, FL 32224
            </p>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      )}
      <p>
        <strong>Note:</strong> Each application must be uploaded as a separate document and must match the enrollment
        type specified.
      </p>
      {application instanceof EnrollmentApplicationWithError && (
        <Alert message="An Error Ocurred While Saving the Enrollment Application" />
      )}
      <div className="upload-enrollment-application__enrollment-actions">
        <Button
          data-testid="upload-application-button"
          className="upload-enrollment-application__submit-button"
          variant="contained"
          disabled={!isValidApplication(application)}
          type="submit"
          color="primary"
          onClick={() => {
            if (application instanceof ValidEnrollmentApplication) {
              dispatch(
                uploadApplication(
                  props.accountId,
                  application,
                  selectedAgency.encryptedIdentifier,
                  enable_portals_virus_scan,
                  SIC_FileNet_Connectivity
                )
              );
            }
          }}
        >
          Submit
        </Button>
        <Button variant="contained" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default withSelectedAgency(EnrollmentApplicationForm);
